import React, { type FC, memo, useState } from 'react';
import { Field } from 'react-final-form';

import { Button, Input, Select } from 'antd';

import FieldWrapper from 'components/FieldWrapper';
import FormSwitch from 'components/FormSwitch';
import FormBlock from 'modules/Common/components/FormBlock';
import FormBlockLine from 'modules/Common/components/FormBlockLine';
import { IOption } from 'types';
import {
	composeValidators,
	requiredFieldValidator,
	textMaxLengthValidator,
} from 'utils/validators';

type UserType = { id: string; fullName: string };

type MainInfoProps = {
	jobTypes: IOption[];
	clientUsers: UserType[];
	jobRestrictedUserIds: number[];
	onOpenModal: () => void;
	isShownCopyButton: boolean;
	isAtsSuperUser: boolean;
	readOnly: boolean;
};

const { Option } = Select;

const MainInfo: FC<MainInfoProps> = ({
	jobTypes,
	clientUsers,
	jobRestrictedUserIds,
	onOpenModal,
	isShownCopyButton = false,
	isAtsSuperUser = false,
	readOnly = false,
}) => {
	const [isRestrictedForUsers, setIsRestrictedForUsers] = useState<boolean>(
		!!jobRestrictedUserIds?.length,
	);

	const handleToggleRestrictedForUsers = () => {
		setIsRestrictedForUsers((prevState) => !prevState);
	};

	return (
		<FormBlock>
			<FormBlockLine>
				<h2 style={{ width: '100%', margin: 0 }}>Main Information</h2>
				<>
					{isShownCopyButton && (
						<Button type='default' htmlType='button' onClick={onOpenModal} size='large'>
							Copy Job
						</Button>
					)}
				</>
			</FormBlockLine>
			<FormBlockLine>
				<Field
					name='title'
					validate={composeValidators(requiredFieldValidator, textMaxLengthValidator)}
				>
					{({ input, meta }) => (
						<FieldWrapper
							name='title'
							label='Title'
							required
							errorMessage={meta.submitFailed && meta.touched && meta.error}
						>
							<Input
								{...input}
								placeholder='e.g., Web Developer'
								autoComplete='off'
								disabled={readOnly}
							/>
						</FieldWrapper>
					)}
				</Field>
				<Field name='jobType' validate={requiredFieldValidator}>
					{({ input, meta }) => (
						<FieldWrapper
							name='jobType'
							label='Job Type'
							required
							errorMessage={meta.submitFailed && meta.touched && meta.error}
						>
							<Select
								{...input}
								value={input.value || null}
								placeholder='Select a job type'
								disabled={readOnly}
							>
								{jobTypes?.map((option: IOption) => (
									<Option key={option.id} value={option.id}>
										{option.name}
									</Option>
								))}
							</Select>
						</FieldWrapper>
					)}
				</Field>
			</FormBlockLine>
			<FormBlockLine>
				<Field
					name='location'
					validate={composeValidators(requiredFieldValidator, textMaxLengthValidator)}
				>
					{({ input, meta }) => (
						<FieldWrapper
							name='location'
							label='Location'
							required
							errorMessage={meta.submitFailed && meta.touched && meta.error}
						>
							<Input {...input} placeholder='e.g., London' autoComplete='off' disabled={readOnly} />
						</FieldWrapper>
					)}
				</Field>
				<Field
					name='salary'
					validate={composeValidators(requiredFieldValidator, textMaxLengthValidator)}
				>
					{({ input, meta }) => (
						<FieldWrapper
							name='salary'
							label='Salary'
							required
							errorMessage={meta.submitFailed && meta.touched && meta.error}
						>
							<Input
								{...input}
								placeholder='e.g., £20,000 - £30,000 per annum'
								autoComplete='off'
								disabled={readOnly}
							/>
						</FieldWrapper>
					)}
				</Field>
			</FormBlockLine>
			<FormBlockLine>
				<Field name='applicationEmail'>
					{({ input, meta }) => (
						<FieldWrapper name='applicationEmail' label='Email for applications'>
							<Input
								{...input}
								placeholder='The email will be auto-generated'
								autoComplete='off'
								disabled
							/>
						</FieldWrapper>
					)}
				</Field>
				<div />
			</FormBlockLine>

			{isAtsSuperUser && (
				<FormBlockLine>
					<FormSwitch
						title='Restrict user visibility'
						name='restrictedForUsers'
						value={isRestrictedForUsers}
						onChange={handleToggleRestrictedForUsers}
					/>
					<Field name='restricted'>
						{({ input }) => <FormSwitch {...input} title='Hide Vacancy on Careers Page' />}
					</Field>
				</FormBlockLine>
			)}
			<>
				{isAtsSuperUser && isRestrictedForUsers && (
					<FormBlockLine>
						<Field name='restrictedUserIds'>
							{({ input, meta }) => (
								<FieldWrapper
									name='restrictedUserIds'
									label='Restricted to'
									errorMessage={meta.submitFailed && meta.touched && meta.error}
								>
									<Select
										{...input}
										mode='multiple'
										defaultValue={[]}
										value={input.value || []}
										options={clientUsers}
										placeholder='Select Users'
										autoClearSearchValue
										fieldNames={{ label: 'fullName', value: 'id' }}
									/>
								</FieldWrapper>
							)}
						</Field>
						<div />
					</FormBlockLine>
				)}
			</>
		</FormBlock>
	);
};

export default memo(MainInfo);
